import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import type { UserNotification } from '@jwp/ott-common/types/notification';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { useNavigationStack } from '@jwp/ott-common/src/stores/NavigationStackStore';

import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import ErrorPage from '../../components/ErrorPage/ErrorPage';
import Button from '../../components/Button/Button';
import Notifications from '../../components/Notifications/Notifications';
import useUserNotifications from '../../hooks/useUserNotifications';
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint';
import { resolveApprovalMaId } from '@jwp/ott-common/src/utils/notificationIds';
import { getApprovalModalView, resolveApprovalMediaType } from '@jwp/ott-common/src/utils/notificationNavigation';
import { shareMediaURL, createURL } from '@jwp/ott-common/src/utils/urlFormatting';

import { createURLFromLocation } from '../../utils/location';
import styles from './NotificationsPage.module.scss';

const NotificationsPage = (): JSX.Element => {
  const { t } = useTranslation(['user', 'common', 'error']);
  const navigate = useNavigate();
  const location = useLocation();
  const breakpoint = useBreakpoint();
  const [clearAllOpen, setClearAllOpen] = useState(false);

  const { user, loading: accountLoading } = useAccountStore((s) => ({
    user: s.user,
    loading: s.loading,
  }));

  const { notifications, loading, loadingMore, error, fetchFailed, hasMore, markAsRead, clearAll, clearingAll, loadMore, refetch } = useUserNotifications();

  const { setAncestor, reset } = useNavigationStack();

  useEffect(() => {
    reset();
    setAncestor(`${window.location.pathname}${window.location.search}`);
  }, [reset, setAncestor]);

  useEffect(() => {
    if (!accountLoading && !user) {
      navigate('/', { replace: true });
    }
  }, [accountLoading, user, navigate]);

  const handleNotificationClick = useCallback(
    async (notification: UserNotification) => {
      if (notification.contentAvailable === false) {
        showToast(t('notifications.content_unavailable'), 'error');
        return;
      }

      try {
        await markAsRead(notification.id);
      } catch {
        showToast(t('common:error_loading_data', { defaultValue: 'Something went wrong. Please try again.' }), 'error');
        return;
      }

      switch (notification.type) {
        case 'LIKE': {
          const mediaId = notification.mediaId?.trim();
          if (!mediaId) {
            showToast(t('notifications.content_unavailable'), 'error');
            return;
          }

          navigate(
            createURL(shareMediaURL(mediaId), {
              returnPath: `${location.pathname}${location.search}`,
            }),
          );
          break;
        }
        case 'MEDIA_APPROVED':
        case 'MEDIA_REJECTED': {
          const maId = resolveApprovalMaId(notification);
          if (!maId) {
            showToast(t('notifications.content_unavailable'), 'error');
            return;
          }
          const maType = await resolveApprovalMediaType(notification, maId);
          useUploadStore.getState().setInitiated(true);
          navigate(
            createURLFromLocation(location, {
              u: getApprovalModalView('details', maType),
              id: maId,
              returnPath: `${location.pathname}${location.search}`,
            }),
          );
          break;
        }
        case 'UPLOAD_REQUEST': {
          if (!hasPermission(user, PERMISSIONS.APPROVALS_MEDIA)) {
            break;
          }
          const maId = resolveApprovalMaId(notification);
          if (!maId) {
            showToast(t('notifications.content_unavailable'), 'error');
            return;
          }
          const maType = await resolveApprovalMediaType(notification, maId);
          useUploadStore.getState().setInitiated(true);
          navigate(
            createURLFromLocation(location, {
              u: getApprovalModalView('review', maType),
              id: maId,
              returnPath: `${location.pathname}${location.search}`,
            }),
          );
          break;
        }
        default:
          break;
      }
    },
    [location, markAsRead, navigate, t, user],
  );

  const handleClearAll = useCallback(async () => {
    try {
      await clearAll();
      setClearAllOpen(false);
    } catch {
      showToast(t('common:error_loading_data', { defaultValue: 'Something went wrong. Please try again.' }), 'error');
    }
  }, [clearAll, t]);

  if (accountLoading || loading) {
    return <LoadingOverlay inline />;
  }

  if (error) {
    return (
      <ErrorPage
        title={t('error:default_error_heading', { defaultValue: 'Something went wrong' })}
        message={t('error:default_error_description', { defaultValue: 'Please try again later.' })}
      />
    );
  }

  return (
    <>
      {fetchFailed && (
        <div className={styles.fetchErrorBanner}>
          <p>{t('notifications.load_error', { defaultValue: 'Unable to load notifications.' })}</p>
          <Button label={t('common:retry', { defaultValue: 'Retry' })} onClick={() => refetch()} size="small" color="default" />
        </div>
      )}
      <Notifications notifications={notifications} onNotificationClick={handleNotificationClick} onClearAllClick={() => setClearAllOpen(true)} />
      {hasMore && (
        <div className={styles.loadMoreContainer}>
          <Button
            label={loadingMore ? t('common:loading') : t('common:load_more')}
            onClick={loadMore}
            disabled={loadingMore}
            color="default"
            fullWidth={breakpoint < Breakpoint.md}
            size="large"
          />
        </div>
      )}
      <ConfirmationDialog
        open={clearAllOpen}
        title={t('notifications.clear_all_title')}
        body={t('notifications.clear_all_body')}
        onConfirm={handleClearAll}
        onClose={() => setClearAllOpen(false)}
        busy={clearingAll}
      />
    </>
  );
};

export default NotificationsPage;
